// AuditPage.tsx — Paginated audit-log drill-in. Action +
// outcome filters seed from the URL (?action= / ?outcome=) so
// deep links land pre-filtered. Cursor pagination is NOT cached;
// DocumentSnapshots aren't safe to stash module-level.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useFirestoreQuery } from '@/hooks/useFirestoreQuery';
import { getLicenseOrg } from '@/services/firestore/licenseOrgs';
import { fetchAuditPage, AUDIT_PAGE_SIZE } from '@/services/firestore/audit';
import type { DocumentSnapshot } from 'firebase/firestore';
import { Breadcrumb } from '@/components/Breadcrumb';
import { SkeletonList } from '@/components/SkeletonRow';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { ActionPill, OutcomePill } from '@/components/pills';
import { fmtAbs, fmtRel, memberLabel } from '@/utils/format';
import type { AuditEvent, LicenseOrg } from '@/types/domain';

export function AuditPage() {
  const { orgId } = useParams<{ orgId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const actionFilter = searchParams.get('action') ?? 'all';
  const outcomeFilter = searchParams.get('outcome') ?? 'all';

  // Org metadata cached - just for the breadcrumb name + the
  // "not found" guard.
  const orgFetcher = useCallback(
    async (): Promise<LicenseOrg | null> =>
      orgId ? getLicenseOrg(orgId) : null,
    [orgId],
  );
  const orgQuery = useFirestoreQuery(
    orgId ? `licenseOrg:meta:${orgId}` : null,
    orgFetcher,
  );

  // Audit rows are NOT cached - cursor objects can't safely live
  // across mounts.
  const [rows, setRows] = useState<AuditEvent[]>([]);
  const [cursor, setCursor] = useState<DocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingFirst, setLoadingFirst] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch the first page on org change.
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setLoadingFirst(true);
    setError(null);
    setRows([]);
    setCursor(null);
    setHasMore(true);
    fetchAuditPage(orgId, null)
      .then((page) => {
        if (cancelled) return;
        setRows(page.events);
        setCursor(page.cursor);
        setHasMore(page.hasMore);
        setLoadingFirst(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoadingFirst(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  async function loadMore() {
    if (!orgId || loadingMore || !hasMore || !cursor) return;
    setLoadingMore(true);
    try {
      const page = await fetchAuditPage(orgId, cursor);
      setRows((prev) => prev.concat(page.events));
      setCursor(page.cursor ?? cursor);
      setHasMore(page.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingMore(false);
    }
  }

  const uniqueActions = useMemo(() => {
    const seen = new Set<string>();
    for (const r of rows) seen.add(String(r.action));
    return Array.from(seen).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (actionFilter !== 'all' && r.action !== actionFilter) return false;
      if (outcomeFilter !== 'all' && r.outcome !== outcomeFilter) return false;
      return true;
    });
  }, [rows, actionFilter, outcomeFilter]);

  if (!orgId) return <ErrorState message="Missing org id in URL" />;
  if (loadingFirst && rows.length === 0 && !orgQuery.data) {
    return <SkeletonList count={5} />;
  }
  if (error && rows.length === 0) {
    return <ErrorState message={`Could not load audit log: ${error}`} />;
  }
  if (orgQuery.data === null) {
    return <ErrorState message={`License org "${orgId}" not found`} />;
  }

  const orgName = orgQuery.data?.name || orgId;
  const orgPath = `/license-orgs/${encodeURIComponent(orgId)}`;
  const rejectedCount = rows.filter((r) => r.outcome === 'rejected').length;

  function updateActionFilter(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('action');
    else next.set('action', value);
    setSearchParams(next, { replace: true });
  }
  function updateOutcomeFilter(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('outcome');
    else next.set('outcome', value);
    setSearchParams(next, { replace: true });
  }

  return (
    <>
      <Breadcrumb
        crumbs={[
          { label: 'License Orgs', to: '/license-orgs' },
          { label: orgName, to: orgPath },
          'Audit log',
        ]}
      />
      <header className="console-page-header">
        <div>
          <h1 className="console-page-title">Audit log</h1>
          <div className="console-page-subtitle">
            {rows.length} event{rows.length === 1 ? '' : 's'} loaded · {rejectedCount} rejected
          </div>
        </div>
      </header>

      <div className="console-filter-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 240 }}>
          <span className="material-icons" style={{ color: 'var(--text-muted)', fontSize: 18 }}>filter_list</span>
          <select
            id="actionFilter"
            className="console-form-input"
            style={{ flex: 1, padding: '8px 36px 8px 12px', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 13 }}
            value={actionFilter}
            onChange={(e) => updateActionFilter(e.target.value)}
          >
            <option value="all">All actions ({rows.length})</option>
            {uniqueActions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        <div className="console-chip-group">
          {(['all', 'success', 'rejected'] as const).map((v) => (
            <button
              key={v}
              className={`console-chip${outcomeFilter === v ? ' is-active' : ''}`}
              type="button"
              onClick={() => updateOutcomeFilter(v)}
            >
              {v[0].toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon="history"
          title="No audit events match"
          body="Adjust the filters above, or generate some events by minting / revoking from the Invites or Members page."
        />
      ) : (
        <AuditTable rows={filtered} />
      )}

      <div style={{ textAlign: 'center', padding: 16 }}>
        {hasMore ? (
          <button
            id="loadMoreBtn"
            className="console-btn console-btn-ghost"
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        ) : (
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>End of feed.</div>
        )}
      </div>
    </>
  );
}

function AuditTable({ rows }: { rows: AuditEvent[] }) {
  return (
    <div className="console-table-wrap">
      <table className="console-table">
        <thead>
          <tr>
            <th>Time</th>
            <th>Action</th>
            <th>Actor</th>
            <th>Target</th>
            <th>Outcome</th>
            <th>Reason</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>
                <div>{fmtRel(r.tsServer)}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }}>
                  {fmtAbs(r.tsServer)}
                </div>
              </td>
              <td>
                <ActionPill action={String(r.action)} />
                <MetadataPreview md={r.metadata} />
              </td>
              <td>
                <ActorBadge actorUid={r.actorUid} actorRole={String(r.actorRole)} />
              </td>
              <td>
                {r.targetKind ? (
                  <>
                    <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {String(r.targetKind)}
                    </div>
                    <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: 'var(--text-secondary)' }}>
                      {r.targetId || '-'}
                    </code>
                  </>
                ) : (
                  '-'
                )}
              </td>
              <td><OutcomePill outcome={String(r.outcome)} /></td>
              <td>
                {r.outcome === 'rejected' && r.reasonCode ? (
                  <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: 'var(--error)' }}>
                    {r.reasonCode}
                  </code>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>-</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActorBadge({ actorUid, actorRole }: { actorUid: string; actorRole: string }) {
  const roleColor =
    actorRole === 'owner' ? '#D4AF37' :
    actorRole === 'admin' ? 'var(--accent-magenta)' :
    actorRole === 'system' ? 'var(--accent-purple)' :
    'var(--text-secondary)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>
        {memberLabel(actorUid)}
      </span>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: roleColor }}>
        {actorRole || '?'}
      </span>
    </div>
  );
}

function MetadataPreview({ md }: { md: Record<string, unknown> }) {
  const keys = Object.keys(md);
  if (!keys.length) return null;
  const pairs = keys.map((k) => {
    let v = md[k];
    if (typeof v === 'string' && v.length > 20) v = `${v.slice(0, 20)}…`;
    return `${k}=${String(v)}`;
  });
  return (
    <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'var(--text-muted)', marginTop: 4, wordBreak: 'break-all' }}>
      {pairs.join(' · ')}
    </div>
  );
}

// Suppress AUDIT_PAGE_SIZE unused-warning when used in JSX-only paths.
void AUDIT_PAGE_SIZE;
